import { useState } from "react";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { CalendarDays, Clock, CheckCircle, ArrowRight, ArrowLeft } from "lucide-react";
import { format, addDays, isBefore, startOfToday } from "date-fns";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BookingSection = ({ onSubmit }) => {
  const [step, setStep] = useState(1);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [availableTimes, setAvailableTimes] = useState([]);
  const [isLoadingTimes, setIsLoadingTimes] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });

  const getTimeSlotsForDay = (date) => {
    const dayOfWeek = date.getDay(); // 0=Sunday, 6=Saturday
    if (dayOfWeek === 0 || dayOfWeek === 6) {
      // Weekend: 11 AM - 4 PM
      return ["11:00", "12:00", "13:00", "14:00", "15:00", "16:00"];
    } else {
      // Weekday: 4 PM - 8 PM
      return ["16:00", "17:00", "18:00", "19:00", "20:00"];
    }
  };

  const formatTime = (time) => {
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour > 12 ? hour - 12 : hour;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const handleDateSelect = async (date) => {
    setSelectedDate(date);
    setSelectedTime(null);
    setIsLoadingTimes(true);
    
    const daySlots = getTimeSlotsForDay(date);
    
    try {
      const dateStr = format(date, "yyyy-MM-dd");
      const response = await axios.get(`${API}/bookings/available-times?date=${dateStr}`);
      setAvailableTimes(response.data.available_times);
    } catch (e) {
      setAvailableTimes(daySlots);
    }
    setIsLoadingTimes(false);
  };

  const handleTimeSelect = (time) => {
    setSelectedTime(time);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await onSubmit({
        ...formData,
        date: format(selectedDate, "yyyy-MM-dd"),
        time: selectedTime,
      });
      setShowConfirmation(true);
    } catch (e) {
      console.error("Booking failed:", e);
    }
    setIsSubmitting(false);
  };

  const resetBooking = () => {
    setStep(1);
    setSelectedDate(null);
    setSelectedTime(null);
    setFormData({ name: "", email: "", phone: "", message: "" });
    setShowConfirmation(false);
  };

  const disabledDays = {
    before: startOfToday(),
  };

  return (
    <section
      id="booking"
      data-testid="booking-section"
      className="py-20 md:py-32 bg-secondary/30"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="uppercase tracking-[0.2em] text-xs font-semibold text-primary">
            Get Started
          </span>
          <h2 className="mt-4 text-3xl md:text-4xl font-bold tracking-tight">
            Book Your Free Discovery Call
          </h2>
          <p className="mt-4 text-muted-foreground max-w-2xl mx-auto">
            Let's discuss your goals and explore how coaching can help you achieve 
            your full potential. This 30-minute call is completely free.
          </p>
        </div>

        {/* Booking Card */}
        <Card className="max-w-4xl mx-auto border-border/50">
          <CardHeader className="text-center pb-2">
            {/* Progress Steps */}
            <div className="flex items-center justify-center gap-4 mb-6">
              {[1, 2, 3].map((s) => (
                <div key={s} className="flex items-center">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-colors ${
                      step >= s
                        ? "bg-primary text-primary-foreground"
                        : "bg-muted text-muted-foreground"
                    }`}
                  >
                    {step > s ? <CheckCircle className="h-5 w-5" /> : s}
                  </div>
                  {s < 3 && (
                    <div
                      className={`w-12 md:w-24 h-0.5 ml-2 ${
                        step > s ? "bg-primary" : "bg-muted"
                      }`}
                    />
                  )}
                </div>
              ))}
            </div>
            <CardTitle className="text-xl">
              {step === 1 && "Select a Date"}
              {step === 2 && "Choose a Time"}
              {step === 3 && "Your Details"}
            </CardTitle>
            <CardDescription>
              {step === 1 && "Pick a date that works for you"}
              {step === 2 && "Select your preferred time slot"}
              {step === 3 && "Fill in your contact information"}
            </CardDescription>
          </CardHeader>

          <CardContent className="pt-6">
            {/* Step 1: Date Selection */}
            {step === 1 && (
              <div className="flex flex-col items-center">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={handleDateSelect}
                  disabled={disabledDays}
                  className="rounded-md border"
                  data-testid="booking-calendar"
                />
                <Button
                  data-testid="booking-next-step-1"
                  onClick={() => setStep(2)}
                  disabled={!selectedDate}
                  className="mt-6"
                >
                  Continue
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            )}

            {/* Step 2: Time Selection */}
            {step === 2 && (
              <div className="space-y-6">
                <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                  <CalendarDays className="h-4 w-4" />
                  <span>{selectedDate && format(selectedDate, "EEEE, MMMM d, yyyy")}</span>
                </div>

                {isLoadingTimes ? (
                  <div className="flex justify-center py-8">
                    <div className="spinner" />
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {(selectedDate ? getTimeSlotsForDay(selectedDate) : []).map((time) => {
                      const isAvailable = availableTimes.includes(time);
                      return (
                        <button
                          key={time}
                          data-testid={`time-slot-${time}`}
                          onClick={() => isAvailable && handleTimeSelect(time)}
                          disabled={!isAvailable}
                          className={`p-4 rounded-lg border text-center transition-all ${
                            selectedTime === time
                              ? "border-primary bg-primary/10 text-primary"
                              : isAvailable
                              ? "border-border hover:border-primary/50 hover:bg-muted/50"
                              : "border-border/30 bg-muted/20 text-muted-foreground cursor-not-allowed"
                          }`}
                        >
                          <Clock className="h-4 w-4 mx-auto mb-1" />
                          <span className="font-medium">{formatTime(time)}</span>
                          {!isAvailable && (
                            <span className="block text-xs mt-1">Booked</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}

                <div className="flex justify-center gap-4">
                  <Button
                    data-testid="booking-back-step-2"
                    variant="outline"
                    onClick={() => setStep(1)}
                  >
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Back
                  </Button>
                  <Button
                    data-testid="booking-next-step-2"
                    onClick={() => setStep(3)}
                    disabled={!selectedTime}
                  >
                    Continue
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}

            {/* Step 3: Contact Form */}
            {step === 3 && (
              <form onSubmit={handleSubmit} className="space-y-6 max-w-md mx-auto">
                <div className="flex items-center justify-center gap-4 text-sm text-muted-foreground pb-4 border-b">
                  <div className="flex items-center gap-1">
                    <CalendarDays className="h-4 w-4" />
                    <span>{selectedDate && format(selectedDate, "MMM d, yyyy")}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Clock className="h-4 w-4" />
                    <span>{selectedTime}</span>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Full Name *</Label>
                    <Input
                      id="name"
                      name="name"
                      data-testid="booking-name-input"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Your full name"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">Email Address *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      data-testid="booking-email-input"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="your@email.com"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone Number</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      data-testid="booking-phone-input"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="+1 (555) 000-0000"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message">What would you like to discuss?</Label>
                    <Textarea
                      id="message"
                      name="message"
                      data-testid="booking-message-input"
                      value={formData.message}
                      onChange={handleInputChange}
                      placeholder="Tell me about your goals and what you'd like to achieve..."
                      rows={4}
                    />
                  </div>
                </div>

                <div className="flex justify-center gap-4">
                  <Button
                    type="button"
                    data-testid="booking-back-step-3"
                    variant="outline"
                    onClick={() => setStep(2)}
                  >
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Back
                  </Button>
                  <Button
                    type="submit"
                    data-testid="booking-submit-btn"
                    disabled={isSubmitting || !formData.name || !formData.email}
                    className="btn-primary"
                  >
                    {isSubmitting ? (
                      <span className="flex items-center gap-2">
                        <div className="spinner w-4 h-4" />
                        Booking...
                      </span>
                    ) : (
                      "Book Discovery Call"
                    )}
                  </Button>
                </div>
              </form>
            )}
          </CardContent>
        </Card>

        {/* Confirmation Dialog */}
        <Dialog open={showConfirmation} onOpenChange={setShowConfirmation}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader className="text-center">
              <div className="mx-auto w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                <CheckCircle className="h-6 w-6 text-primary" />
              </div>
              <DialogTitle>Booking Confirmed!</DialogTitle>
              <DialogDescription>
                Your discovery call has been scheduled for{" "}
                <strong>{selectedDate && format(selectedDate, "MMMM d, yyyy")}</strong> at{" "}
                <strong>{selectedTime && formatTime(selectedTime)}</strong>. You'll receive a confirmation email shortly.
              </DialogDescription>
            </DialogHeader>
            
            {/* Zoom Meeting Details */}
            <div className="mt-4 p-4 bg-primary/5 rounded-lg border border-primary/20">
              <h4 className="font-semibold text-sm mb-2">Join via Zoom:</h4>
              <p className="text-sm text-muted-foreground mb-2">
                Deepa Prashanth is inviting you to a scheduled Zoom meeting.
              </p>
              <a 
                href="https://us04web.zoom.us/j/2919274046?pwd=d4e5omUhC3WhDx50sDUaB2CGnsuIEd.1&omn=71447932953"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block text-sm text-primary hover:underline font-medium mb-2"
              >
                Join Zoom Meeting →
              </a>
              <div className="text-xs text-muted-foreground space-y-1">
                <p><strong>Meeting ID:</strong> 291 927 4046</p>
                <p><strong>Passcode:</strong> cG9ceF</p>
              </div>
            </div>
            
            <div className="flex justify-center mt-4">
              <Button data-testid="booking-done-btn" onClick={resetBooking}>
                Done
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </section>
  );
};

export default BookingSection;
